import { QueryClient, useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

/** Thin client for the management API. Same-origin; the session is an httpOnly cookie
 * and every state-changing call carries the CSRF token from /auth/me. */
let csrf = '';
export function setCsrf(t: string) {
  csrf = t;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Array<{ path: string; message: string }>,
  ) {
    super(message);
  }
}

export async function api<T = any>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(method !== 'GET' ? { 'x-csrf-token': csrf } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (res.status === 401 && !path.startsWith('/auth/')) {
    window.location.assign('/login');
    throw new ApiError(401, 'unauthorized', 'Signed out');
  }
  const ct = res.headers.get('content-type') ?? '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const b = body as { error?: string; message?: string; details?: ApiError['details'] };
    throw new ApiError(res.status, b?.error ?? 'error', b?.message ?? `Request failed (${res.status})`, b?.details);
  }
  return body as T;
}

/** Downloads a file produced by the API: POST with `body`, or a plain GET (no CSRF token needed) when `body` is undefined. */
export async function download(path: string, body: unknown, fallbackName: string) {
  const get = body === undefined;
  const res = await fetch(
    `/api/v1${path}`,
    get
      ? { method: 'GET', credentials: 'same-origin', headers: { accept: 'application/json' } }
      : { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'x-csrf-token': csrf }, body: JSON.stringify(body) },
  );
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new ApiError(res.status, j.error ?? 'error', j.message ?? (get ? `Download failed (${res.status})` : 'Download failed'));
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: false } },
});

export function useApi<T = any>(path: string | null, opts: Partial<UseQueryOptions<T>> = {}) {
  return useQuery<T>({ queryKey: [path], queryFn: () => api<T>(path!), enabled: path !== null, ...opts });
}

/** Mutation that invalidates everything on success (cheap at this scale, always consistent). */
export function useAction<V = any, R = any>(fn: (v: V) => Promise<R>, onDone?: (r: R) => void) {
  const qc = useQueryClient();
  return useMutation<R, ApiError, V>({
    mutationFn: fn,
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      onDone?.(r);
    },
  });
}

export function qs(params: Record<string, string | number | boolean | undefined | null | string[]>) {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    u.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}
